import { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { Box, Button, Page } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { ModalTenantSelect } from '@zg-rentals/modal-tenant';
import { useQueryClient } from '@zg-rentals/react-query5';

import { FlexiblePropertiesSearchProvider } from '../../contexts/FlexiblePropertiesSearchContext';
import { useGetPropertyTableList } from '../../hooks/queries/propertyTable';
import { usePropertiesPageUserOnboarding } from '../../hooks/usePropertiesPageUserOnboarding';
import accessUtils from '../../utils/accessUtils';
import {
  defaultFlexiblePropertiesFilters,
  initializeFlexiblePropertiesPageStateFromQuery,
} from '../../utils/flexiblePropertiesPageUtils';
import {
  isRedirectToPayments,
  removeQueryParams,
  renderInitialDisplayModal,
  transformFiltersToApiParams,
} from '../../utils/propertiesPageUtils';

import {
  DEFAULT_FLEXIBLE_PROPERTIES_VIEW_MODE,
  FLEXIBLE_PROPERTIES_VIEW_MODE,
  type FlexiblePropertiesViewMode,
} from '../../constants/FlexiblePropertiesConstants';
import PropertiesConstants from '../../constants/PropertiesConstants';
import QueryConstants from '../../constants/QueryConstants';
import { EXTERNAL_ROUTES, getPaymentsDashboardRoute } from '../../constants/urls';

import { useAuth } from '../../components/AuthProvider';
import AddPropertyFlowModal from '../../components/modals/AddPropertyFlowModal';
import PaymentsAggregateViewModal from '../../components/modals/PaymentsAggregateViewModal';
import UserOnboarding from '../../components/modals/UserOnboarding';
import EmailVerification from '../../components/properties/EmailVerification';
import NotificationBanner from '../../components/properties/NotificationBanner';
import { PropertiesPageNotifications } from '../../components/properties/PropertiesPageNotifications';
import { FlexiblePropertyCardList } from './components/FlexiblePropertyCardList';
import { FlexiblePropertyEmptyState } from './components/FlexiblePropertyEmptyState';
import { FlexiblePropertySearchEmptyState } from './components/FlexiblePropertySearchEmptyState';
import PropertiesTable from './components/PropertiesTable';
import { Toolbar } from './components/Toolbar';

import { emitViewPropertiesPageEvent } from '../../analytics/events/propertiesPage';
import { createZgApiHttpClient } from '../../clients/zgApiHttpClient';
import AuthCheckWrapper from '../AuthCheckWrapper';
import { PageHeading } from '../PropertiesPage/PageHeading';
import { PropertiesPageSkeleton } from '../PropertiesPage/PropertiesPageSkeleton';

import type { FlexiblePropertiesFilterState } from '../../utils/flexiblePropertiesPageUtils';

/*
 * TODO: Parity gaps vs PropertiesPage:
 *
 * 1. Analytics - emitViewPropertiesPageEvent on mount, data-eventid on Page.Root,
 *    and add-property clickstream events.
 */
const FlexiblePropertiesPage = () => {
  const router = useRouter();
  const { user, isAuthorized } = useAuth();

  const isCheckInitialDisplayModal = useRef(true);
  const hasInitializedFromQuery = useRef(false);

  const queryClient = useQueryClient();
  const client = createZgApiHttpClient(process.env.NEXT_PUBLIC_ZG_API_PUBLIC ?? '');

  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [tenantModalListingAlias, setTenantModalListingAlias] = useState<string | null>(null);
  const [showPaymentsAggregateViewModal, setShowPaymentsAggregateViewModal] = useState(false);
  const [filters, setFilters] = useState<FlexiblePropertiesFilterState>(defaultFlexiblePropertiesFilters);
  const [viewMode, setViewMode] = useState<FlexiblePropertiesViewMode>(DEFAULT_FLEXIBLE_PROPERTIES_VIEW_MODE);

  useEffect(() => {
    if (!router.isReady || hasInitializedFromQuery.current) {
      return;
    }

    hasInitializedFromQuery.current = true;

    const { filters: initialFilters, viewMode: initialViewMode } = initializeFlexiblePropertiesPageStateFromQuery(
      router.query,
    );

    setFilters(initialFilters);
    setViewMode(initialViewMode);
  }, [router.isReady]);

  const skeleton = <PropertiesPageSkeleton />;

  const {
    data: propertiesData,
    error: propertiesError,
    isError,
    isPending,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetPropertyTableList(
    transformFiltersToApiParams({
      subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
      searchText: filters.searchText,
    }),
    {
      enabled: isAuthorized && Boolean(user?.restrictions?.isAllowed),
    },
  );

  const { listingCount, listingGroups } = propertiesData;

  const { total: numTotalListings = 0, archived: numArchivedListings = 0 } = listingCount ?? {};

  const {
    canAccessUserOnboardingNewUser,
    setShowingUserOnboardingExisitngUser,
    canAccessUserOnboardingExistingUser,
    onboardingConfig,
  } = usePropertiesPageUserOnboarding({ numTotalListings });

  const isContentLoading = isPending || isFetching || isFetchingNextPage;
  // The very first fetch, before any data has resolved. Gated on `isFetching` so a disabled
  // query (e.g. restricted user) doesn't get stuck showing skeletons forever.
  const isInitialLoading = isPending && isFetching;
  const hasAnyListings = numTotalListings > 0 || numArchivedListings > 0;
  // listingCount is the unfiltered portfolio summary, while listingGroups is filtered by search; an active
  // search with no matching groups is a "no results" state, distinct from the initial empty state.
  const hasNoSearchResults = Boolean(filters.searchText) && (listingGroups?.length ?? 0) === 0 && !isContentLoading;

  useEffect(() => {
    emitViewPropertiesPageEvent({ user });
  }, []);

  useEffect(() => {
    if (
      user?.features?.canAccessMainPayments &&
      !user?.hasSeenPaymentsAggregatedViewModal &&
      (user?.paymentsAcceptedCount ?? 0) >= 2
    ) {
      setShowPaymentsAggregateViewModal(true);
    }
  }, [user?.features?.canAccessMainPayments]);

  useEffect(() => {
    if (isCheckInitialDisplayModal.current && !isPending) {
      isCheckInitialDisplayModal.current = false;
      checkInitialDisplayModal();
    }
  }, [isPending, numTotalListings]);

  useEffect(() => {
    if (isError && propertiesError) {
      logError({
        error: propertiesError,
        context: 'fetchGroupableProperties',
      });
    }
  }, [isError, propertiesError]);

  useEffect(() => {
    const postingPath = router.query[QueryConstants.POSTING_PATH];

    if (postingPath) {
      checkInitialDisplayModal();
    }
  }, [router.query[QueryConstants.POSTING_PATH]]);

  const checkInitialDisplayModal = () => {
    renderInitialDisplayModal({
      router,
      totalListingsWithoutFilters: numTotalListings,
      showAddPropertyModal,
      setShowAddPropertyModal: handleAddPropertyClick,
      canAccessUserOnboardingNewUser,
      setShowingUserOnboardingExisitngUser,
      canAccessUserOnboardingExistingUser,
    });
  };

  const handleClearSearch = () => {
    setFilters((current) => ({ ...current, searchText: '' }));
  };

  const handleAddPropertyClick = () => {
    if (user?.features?.canAddListing && user?.restrictions?.isAllowed) {
      setShowAddPropertyModal(true);
    }

    // TODO: add flexible properties analytics
    // emitOpenEvent({ user: user as ZrmClickstreamUser });
    // emitAddPropertyClickEvent({ user: user as ZrmClickstreamUser });
  };

  const canAccessFeatureNotifications = user ? accessUtils.canAccessFeatureNotifications({ user }) : false;

  const renderPropertiesView = () => {
    const sharedProps = { listingGroups: listingGroups ?? [], isLoading: isContentLoading };

    switch (viewMode) {
      case FLEXIBLE_PROPERTIES_VIEW_MODE.PROPERTIES:
        return <PropertiesTable {...sharedProps} />;

      // TODO: add units view
      case FLEXIBLE_PROPERTIES_VIEW_MODE.UNITS:
      case FLEXIBLE_PROPERTIES_VIEW_MODE.BOTH:
      default:
        return <FlexiblePropertyCardList {...sharedProps} />;
    }
  };

  return (
    <AuthCheckWrapper loading={skeleton} redirecting={skeleton}>
      <Page.Root data-testid="FlexiblePropertiesPage" css={{ backgroundColor: 'bg.neutral' }}>
        <Head>
          <title>Properties | Zillow Rental Manager</title>
        </Head>

        {canAccessFeatureNotifications && <NotificationBanner />}

        {/* Page Heading */}
        <PageHeading
          buttonEmphasis="filled"
          buttonIconPosition="start"
          buttonIcon={<IconPlusFilled />}
          buttonSize="sm"
          headingTextStyle="heading-xs"
          handleAddPropertyClick={handleAddPropertyClick}
        />

        {/* Email verification and notifications */}
        <EmailVerification />
        <PropertiesPageNotifications />

        {/* Content / Tables */}
        <Page.Content css={{ padding: { base: '0 {spacing.600}', xl: '0 {spacing.1200}' } }}>
          {/* Has listings or initial loading */}
          {(hasAnyListings || isInitialLoading) && (
            <>
              <Toolbar
                searchText={filters.searchText}
                onSearchTextChange={(searchText) => setFilters((current) => ({ ...current, searchText }))}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isLoading={isContentLoading}
                isSearching={isContentLoading && Boolean(filters.searchText)}
              />

              <FlexiblePropertiesSearchProvider searchText={filters.searchText}>
                {hasNoSearchResults ? (
                  <FlexiblePropertySearchEmptyState searchText={filters.searchText} onClearSearch={handleClearSearch} />
                ) : (
                  renderPropertiesView()
                )}
              </FlexiblePropertiesSearchProvider>
            </>
          )}

          {/* Empty state */}
          {!hasAnyListings && !isContentLoading && (
            <FlexiblePropertyEmptyState onAddPropertyClick={handleAddPropertyClick} />
          )}

          {hasNextPage && (
            <Box css={{ display: 'flex', justifyContent: 'center', marginTop: '600' }}>
              <Button tone="brand" emphasis="filled" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
                Load more properties
              </Button>
            </Box>
          )}
        </Page.Content>

        {/* Modals */}
        {onboardingConfig && <UserOnboarding {...onboardingConfig} />}

        {showAddPropertyModal && (
          <AddPropertyFlowModal
            isOpen={showAddPropertyModal}
            onClose={() => {
              setShowAddPropertyModal(false);
              removeQueryParams(router, [QueryConstants.POSTING_PATH, QueryConstants.REDIRECT_TO]);
            }}
            onConfirm={(listingAlias) => {
              if (isRedirectToPayments(router.query)) {
                window.location.href = getPaymentsDashboardRoute({ listingAlias });
              }
            }}
            onAddTenant={setTenantModalListingAlias}
          />
        )}
        {showPaymentsAggregateViewModal && (
          <PaymentsAggregateViewModal
            onClose={() => setShowPaymentsAggregateViewModal(false)}
            paymentsAggregatePageUrl={EXTERNAL_ROUTES.PAYMENTS_DASHBOARD}
          />
        )}
        {tenantModalListingAlias && (
          <ModalTenantSelect
            client={client}
            listingAlias={tenantModalListingAlias}
            isOpen={true}
            queryClient={queryClient}
            onAddRenterSuccess={() => undefined}
            onClose={() => setTenantModalListingAlias(null)}
            onPrimaryCTAClick={() => setTenantModalListingAlias(null)}
          />
        )}
      </Page.Root>
    </AuthCheckWrapper>
  );
};

export default FlexiblePropertiesPage;
